"use client";

import Link from "next/link";
import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { BASE_URL } from "@/lib/constant";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function Signup() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNo, setPhoneNo] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const router = useRouter();

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (!fullName || !email || !password) {
      setError("Please fill in all fields.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Name: fullName,
          email,
          password,
          Phone_Number: phoneNo,
        }),
      }).then((resp) => resp.json());

      console.log(response);

      // Check if the response was successful
      if (response.message) {
        toast("Success!", {
          description: "Your account has been created successfully.",
          style: {
            color: "green",
          },
        });
        // Redirect to the sign-in page or dashboard
        router.push("/signin");
      } else {
        // Handle API errors
        throw new Error(response.detail || "Something went wrong.");
      }
    } catch (err) {
      let message = "An unexpected error occurred.";

      if (err instanceof Error) {
        message = err.message;
      } else if (
        err &&
        typeof err === "object" &&
        "message" in err &&
        typeof (err as Record<string, unknown>).message === "string"
      ) {
        message = (err as Record<string, unknown>).message as string;
      }

      setError(message);

      toast("Unable to authenticate!", {
        description: message,
        style: {
          color: "red",
        },
        descriptionClassName: "red",
      });
    } finally {
      // Reset loading state
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-6 py-16 flex justify-center items-center">
      <div className="w-full max-w-md bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
        <h1 className="text-3xl font-bold text-center mb-6 text-gray-900">
          Create Account
        </h1>
        {/* Display a generic error message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-4">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        <form onSubmit={handleSignUp} className="space-y-6">
          <div>
            <Label
              htmlFor="fullName"
              className="block text-sm font-medium text-gray-700"
            >
              Full Name
            </Label>
            <Input
              id="fullName"
              type="text"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="mt-1 w-full p-3 rounded-lg border bg-white/50 border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <Label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full p-3 rounded-lg border bg-white/50 border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <Label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700"
            >
              Phone Number
            </Label>
            <Input
              id="phone"
              type="text"
              required
              value={phoneNo}
              onChange={(e) => setPhoneNo(e.target.value)}
              className="mt-1 w-full p-3 rounded-lg border bg-white/50 border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <Label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700"
            >
              Password
            </Label>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full p-3 rounded-lg border bg-white/50 border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 disabled:bg-green-400 transition duration-300 shadow-md"
          >
            {isLoading ? "Creating Account..." : "Create Account"}
          </Button>
        </form>
        <p className="text-center mt-6 text-gray-600">
          Already have an account?{" "}
          <Link
            href="/signin"
            className="font-semibold text-green-600 hover:text-green-700"
          >
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Signup;
